import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FileText, Truck, ListChecks, Package, AlertTriangle, BellRing } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { KpiTile, ActivePoTable, DispatchTable, TasksTable, StockTable, DashHeader, MomStrip } from "@/components/DashboardKit";

export default function DispatchDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notified, setNotified] = useState(() => new Set());
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/dispatch")
      .then((r) => mounted && setData(r.data))
      .catch(() => toast.error("Failed to load dispatch dashboard"))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!data) return;
    const batch = [];
    for (const t of data.tasks || []) if (t.overdue) batch.push({ k: `t-${t.id}`, m: `Overdue task: ${t.task_title}` });
    for (const d of data.dispatches || []) if (d.delivery_status === "Pending" && d.payment_pending === "Yes") batch.push({ k: `pay-${d.id}`, m: `Payment pending for PO ${d.po_number}` });
    const fresh = batch.filter((b) => !notified.has(b.k)).slice(0, 5);
    if (fresh.length) {
      fresh.forEach((n) => toast.warning(n.m));
      setNotified((prev) => { const n = new Set(prev); fresh.forEach((f) => n.add(f.k)); return n; });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (loading) return <div className="p-6 space-y-4"><Skeleton className="h-10 w-80" /><div className="grid grid-cols-4 gap-3">{[1,2,3,4].map(i=><Skeleton key={i} className="h-24" />)}</div><Skeleton className="h-80" /></div>;
  if (!data) return null;
  const s = data.summary || {};

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="dispatch-dashboard-page">
      <DashHeader
        eyebrow="Dispatch Dashboard"
        title={`Hello ${user?.name?.split(" ")[0]} — your dispatch cockpit`}
        hint="Active POs for dispatch, pending/completed dispatches, assigned tasks and stock available to ship."
        right={<div className="flex gap-2">
          {s.overdue_tasks > 0 && <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50 gap-1"><AlertTriangle className="h-3 w-3" />{s.overdue_tasks} overdue</Badge>}
          {s.dispatch_pending > 0 && <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50 gap-1"><BellRing className="h-3 w-3" />{s.dispatch_pending} to ship</Badge>}
        </div>}
      />

      <MomStrip items={data.mom} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
        <KpiTile label="Active POs" value={s.active_pos} icon={FileText} accent="blue" testid="kpi-active-pos" />
        <KpiTile label="Dispatch Pending" value={s.dispatch_pending} sub={`${s.dispatch_transit} in transit`} icon={Truck} accent="amber" testid="kpi-dispatch-pending" />
        <KpiTile label="Delivered" value={s.dispatch_delivered} icon={Package} accent="emerald" testid="kpi-delivered" />
        <KpiTile label="My Pending Tasks" value={s.pending_tasks} sub={`${s.overdue_tasks} overdue · ${s.due_soon_tasks} due soon`} icon={ListChecks} accent="rose" testid="kpi-tasks" />
      </div>

      <Tabs defaultValue="pos" data-testid="dispatch-tabs">
        <TabsList className="h-auto flex-wrap bg-slate-100 p-1">
          <TabsTrigger value="pos" data-testid="tab-active-pos"><FileText className="h-3.5 w-3.5 mr-1.5" /> Active PO</TabsTrigger>
          <TabsTrigger value="status" data-testid="tab-dispatch"><Truck className="h-3.5 w-3.5 mr-1.5" /> Dispatch Status</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks"><ListChecks className="h-3.5 w-3.5 mr-1.5" /> Pending Task</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock"><Package className="h-3.5 w-3.5 mr-1.5" /> Production Stock</TabsTrigger>
        </TabsList>

        <TabsContent value="pos"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Active POs needing dispatch</div>
          <ActivePoTable rows={data.active_pos || []} />
        </CardContent></Card></TabsContent>

        <TabsContent value="status"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Dispatch Pipeline</div>
          <DispatchTable rows={data.dispatches || []} />
        </CardContent></Card></TabsContent>

        <TabsContent value="tasks"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Assigned Tasks</div>
            <div className="flex gap-2">
              <Badge variant="outline" className="border-red-200 text-red-700 bg-red-50">{s.overdue_tasks} overdue</Badge>
              <Badge variant="outline" className="border-amber-200 text-amber-700 bg-amber-50">{s.due_soon_tasks} due 7d</Badge>
            </div>
          </div>
          <TasksTable rows={data.tasks || []} />
        </CardContent></Card></TabsContent>

        <TabsContent value="stock"><Card className="border-slate-200"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">Production Stock</div>
          <StockTable rows={data.stocks || []} />
        </CardContent></Card></TabsContent>
      </Tabs>
    </div>
  );
}
